import type {
  DemoEnvironmentRunResponse,
  DemoEnvironmentRunStep,
} from '../../services/api/types.gen';

/**
 * The demo setup run checklist.
 *
 * The steps come from the run row, which the worker updates as each step
 * finishes. The step order here has to match the backend's
 * `DEMO_AGENCY_SETUP_STEP_ORDER`; it only fills in the steps a running run has
 * not reached yet.
 */

/** Matches backend `DEMO_AGENCY_SETUP_STEP_ORDER`. */
export const DEMO_SETUP_STEP_ORDER = [
  'validate',
  'wipe_test_data',
  'configure_agency',
  'migrate_kinnser_clinics',
  'seed_intakes',
  'seed_completed_charts',
  'seed_qa_hotbox',
  'seed_billing_demo',
  'seed_reporting_demo',
  'seed_rogerdoc_coding',
] as const;

export const STEP_LABELS: Record<string, string> = {
  validate: 'Validate agency',
  wipe_test_data: 'Wipe test data',
  configure_agency: 'Configure agency',
  migrate_kinnser_clinics: 'Migrate Kinnser clinics',
  seed_intakes: 'Seed intakes',
  seed_billing_demo: 'Seed billing / NOA demo',
  seed_completed_charts: 'Seed completed charts',
  seed_qa_hotbox: 'Seed QA hotbox',
  seed_reporting_demo: 'Seed reporting demo',
  seed_rogerdoc_coding: 'Seed RogerDoc coding',
  // The nightly refresh re-applies these two after preprod RogerDoc is
  // replaced from prod.
  refresh_rogerdoc_coding: 'Restore RogerDoc coding',
  refresh_rogerdoc_flags: 'Restore RogerDoc feature flags',
};

const RUN_KIND_LABELS: Record<DemoEnvironmentRunResponse['kind'], string> = {
  setup: 'Setup',
  refresh: 'Refresh',
};

const RUN_STATUS_LABELS: Record<DemoEnvironmentRunResponse['status'], string> =
  {
    running: 'running',
    succeeded: 'succeeded',
    failed: 'failed',
  };

/** "Setup failed", "Refresh succeeded": which kind of run, and how it went. */
export function runLabel(run: DemoEnvironmentRunResponse): string {
  return `${RUN_KIND_LABELS[run.kind]} ${RUN_STATUS_LABELS[run.status]}`;
}

export interface MessagePart {
  label: string | null;
  text: string;
}

/**
 * A step message split into lines for display.
 *
 * Step messages pack several results into one string, joined with "; ", and
 * many parts read "what: result" ("clinic sync: 1 clinic(s)"). Splitting there
 * lets the checklist show one result per line with its label picked out.
 */
export function messageParts(
  message: string | null | undefined
): MessagePart[] {
  if (!message) return [];
  return message
    .split(/;\s+/)
    .filter(Boolean)
    .map(part => {
      const match = /^([^:]{1,30}):\s+(.+)$/.exec(part);
      return match
        ? { label: match[1], text: match[2] }
        : { label: null, text: part };
    });
}

/**
 * The steps to show for a run.
 *
 * A finished run shows exactly what it saved. A running setup also lists the
 * steps it has not reached yet as pending, so the operator can see how far it
 * has to go. A refresh has its own two steps, so it is never padded with setup
 * steps.
 */
export function displaySteps(
  run: DemoEnvironmentRunResponse
): DemoEnvironmentRunStep[] {
  if (run.status !== 'running' || run.kind !== 'setup') return run.steps;
  const reached = new Set(run.steps.map(step => step.id));
  const remaining = DEMO_SETUP_STEP_ORDER.filter(id => !reached.has(id)).map(
    id => ({ id, status: 'pending' as const, message: null })
  );
  return [...run.steps, ...remaining];
}
